// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Input, Label, Translation } from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { useRefundFields, type FormWithRefundFields } from './use-refund-fields';

export const RefundFields = defineComponent({
  displayName: 'RefundFields',
  useSetup: useRefundFields,
  View: ({
    control,
    readOnly = false,
    disabled = false,
    currencyPrefix,
    onBlur,
    'data-testid': dataTestId,
  }) => (
    <div
      className="flex flex-1 items-start justify-end gap-4"
      data-testid={dataTestId}
    >
      <div className="flex flex-col gap-1" data-testid="percentage-field">
        <Label className="text-center" data-testid="percentage-label">
          %
        </Label>
        <Controller<FormWithRefundFields, 'percentage'>
          control={control}
          name="percentage"
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              value={field.value}
              name="percentage"
              onValueChange={({ floatValue }) => field.onChange(floatValue ?? 0)}
              onBlur={e => {
                field.onBlur();
                onBlur?.(e);
              }}
              readOnly={readOnly}
              disabled={disabled}
              className="w-[70px] text-center"
              data-testid="percentage-input"
              allowNegative={false}
              min={0}
              max={100}
              decimalScale={2}
              suffix="%"
              allowedDecimalSeparators={DECIMAL_SEPARATORS}
            />
          )}
        />
      </div>
      <div className="flex flex-col gap-1" data-testid="shares-field">
        <Label className="text-center" data-testid="shares-label">
          <Translation ns="components" i18nKey="ticker-modal.shares-label" />
        </Label>
        <Controller<FormWithRefundFields, 'shares'>
          control={control}
          name="shares"
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              value={field.value}
              name="shares"
              onValueChange={({ floatValue }) => field.onChange(floatValue ?? 0)}
              onBlur={e => {
                field.onBlur();
                onBlur?.(e);
              }}
              readOnly={readOnly}
              disabled={disabled}
              className="w-[120px] text-center"
              data-testid="shares-input"
              allowNegative={false}
              decimalScale={2}
              allowedDecimalSeparators={DECIMAL_SEPARATORS}
            />
          )}
        />
      </div>
      <div className="flex flex-col gap-1" data-testid="amount-field">
        <Label className="text-center" data-testid="amount-label">
          <Translation ns="components" i18nKey="ticker-modal.amount-label" />
        </Label>
        <Controller<FormWithRefundFields, 'amount'>
          control={control}
          name="amount"
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              value={field.value}
              name="amount"
              onValueChange={({ floatValue }) => field.onChange(floatValue ?? 0)}
              onBlur={e => {
                field.onBlur();
                onBlur?.(e);
              }}
              readOnly={readOnly}
              disabled={disabled}
              className="w-[120px] text-center"
              data-testid="amount-input"
              prefix={currencyPrefix}
              allowNegative={false}
              decimalScale={0}
              fixedDecimalScale={false}
              allowedDecimalSeparators={DECIMAL_SEPARATORS}
              thousandSeparator
            />
          )}
        />
      </div>
    </div>
  ),
});
